import { Box, Typography } from '@mui/material'
import colors from 'styles/colors'
import { formatDate } from 'utils/formatDateTime'
import RowStack from 'components/atoms/container/RowStack'
import { useTranslation } from 'react-i18next'
import { ColStack } from 'components/atoms/container/InCardStack'
import { Tag } from 'components/atoms/Text'
import { IconContainer } from 'components/atoms/Icons'
import BlankRect from 'components/atoms/BlankRect'
import PsychologyOutlinedIcon from '@mui/icons-material/PsychologyOutlined'
import TourOutlinedIcon from '@mui/icons-material/TourOutlined'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import QueryBuilderIcon from '@mui/icons-material/QueryBuilder'
import Dot from 'components/atoms/Dot'
import { formatSeconds } from 'utils/formatDateTime'
const defaultGoalImg = require('assets/images/defaultGoal.png')

export const CardTag = ({
  children,
  showEllipsis = true,
  sx = {},
  ...otherProps
}) => {
  const tagEllipsis = {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '75%'
  }
  return (
    <Tag
      sx={{
        padding: 2,
        lineHeight: '120%',
        backgroundColor: colors.MetalGrey50,
        ...(showEllipsis && tagEllipsis),
        ...sx
      }}
      {...otherProps}>
      {children}
    </Tag>
  )
}

export const GoalImageContainer = ({ goal, isLibrary, sx = {}, children }) => {
  const goalImage = goal.image || defaultGoalImg
  const completedNotInLibrary =
    (goal?.progress?.completed || goal.opensAt) && !isLibrary
  const background = completedNotInLibrary
    ? `linear-gradient(0deg, rgba(57, 89, 102, 0.75) 0%, rgba(57, 89, 102, 0.75) 100%), url(${goalImage}) lightgray 0px -0.012px / 100% 118.802% no-repeat;`
    : `url(${goalImage})`
  return (
    <ColStack
      sx={{
        width: '100%',
        height: 165,
        background,
        backgroundSize: 'cover',
        borderRadius: 1,
        overflow: 'hidden',
        padding: 8,
        justifyContent: 'space-between',
        ...sx
      }}>
      {children}
    </ColStack>
  )
}

export const CardHeader = ({ goal, isLibrary }) => {
  const { t } = useTranslation()
  const goalProgress = goal?.progress
  return (
    <GoalImageContainer goal={goal} isLibrary={isLibrary}>
      {!isLibrary ? (
        goalProgress?.completed ? (
          <>
            {goalProgress?.completedAt && (
              <CardTag sx={{ color: colors.MidnightTeal300 }}>
                {formatDate(goalProgress?.completedAt)}
              </CardTag>
            )}

            <Typography
              variant='h3'
              sx={{ color: colors.White, alignSelf: 'center' }}>
              {t('common.completed')}
            </Typography>
          </>
        ) : goal?.overDue ? (
          <CardTag
            sx={{ backgroundColor: colors.ErrorRed, color: colors.White }}>
            {t('pathsAndModels.overdue')}
          </CardTag>
        ) : goal?.currentGoal ? (
          <CardTag>{t('checkInSurvey.current')}</CardTag>
        ) : goal.opensAt ? (
          <>
            <Box />
            <Typography
              variant='h3'
              sx={{ color: colors.White, alignSelf: 'center' }}>
              {t('home.opens')} {formatDate(goal.opensAt)}
            </Typography>
          </>
        ) : (
          <Box />
        )
      ) : (
        <Box />
      )}

      <CardTag
        sx={{
          paddingX: 3,
          paddingY: 1,
          backgroundColor: colors.MutedTeal50
        }}
        component={'div'}>
        <RowStack spacing={1}>
          <IconContainer icon={<PsychologyOutlinedIcon />} size={16} />
          <Typography variant='body1Small' noWrap>
            {goal.leadershipModelName}
          </Typography>
        </RowStack>
      </CardTag>
    </GoalImageContainer>
  )
}

export const CardFooter = ({ goal, isLibrary }) => {
  const { t } = useTranslation()
  const totalMilestones = goal.l1Steps?.length - 1
  const TimeText = () => {
    return (
      <RowStack spacing={2} height={'100%'} width={'auto'}>
        <IconContainer icon={<QueryBuilderIcon />} />
        <Typography
          variant='body1Small'
          dangerouslySetInnerHTML={{
            __html: goal.completed
              ? t('common.completed')
              : goal?.stats?.remainingSeconds
                ? formatSeconds(goal?.stats?.remainingSeconds, undefined, true)
                : formatSeconds(goal?.stats?.goalLengthSeconds, undefined, true)
          }}
        />
      </RowStack>
    )
  }
  return (
    <RowStack spacing={0} height={25} justifyContent={'space-between'}>
      <RowStack spacing={2} height={'100%'} width={'auto'}>
        {goal.l1Steps?.length > 1 ? ( // Ignore the last milestone (Completion)
          <>
            <IconContainer icon={<TourOutlinedIcon />} />

            {!isLibrary &&
            (goal?.stats?.numberOfCompletedL1Steps || goal.completed) ? (
              <Typography variant='body1Small'>
                {t('home.nofNMilestones', {
                  completed: goal.completed
                    ? totalMilestones
                    : goal?.stats?.numberOfCompletedL1Steps,
                  total: totalMilestones
                })}
              </Typography>
            ) : (
              <Typography variant='body1Small'>
                {`${totalMilestones} ${t('home.milestone')}${goal.l1Steps?.length > 2 ? 's' : ''}`}
              </Typography>
            )}
          </>
        ) : (
          <CardSkeleton w={120} />
        )}
      </RowStack>

      {!isLibrary &&
        (goal?.stats?.remainingSeconds || goal.completed || goal.opensAt) && (
          <TimeText />
        )}
    </RowStack>
  )
}

export const PopupSubHeader = ({
  l1Milestones,
  skills,
  goalLengthSeconds,
  goalRemainingSeconds,
  isActive
}) => {
  const { t } = useTranslation()
  const displayTime =
    isActive && goalRemainingSeconds ? goalRemainingSeconds : goalLengthSeconds
  const formattedTime = formatSeconds(displayTime, undefined, true)
  const goalRemaining =
    isActive && goalRemainingSeconds ? ` ${t('home.remaining')}` : ''

  const typoProps = {
    variant: 'body1Small',
    sx: { color: colors.MidnightTeal300, fontWeight: 500 }
  }
  return (
    <RowStack spacing={4}>
      <Typography {...typoProps}>
        <strong>{l1Milestones.length}</strong> {t('home.milestone')}
        {l1Milestones?.length > 2 ? 's' : ''}
      </Typography>
      <Dot />
      <Typography {...typoProps}>
        <strong>{skills.length}</strong> {t('home.skill')}
        {skills?.length > 1 ? 's' : ''}
      </Typography>
      <Dot />
      <Typography
        {...typoProps}
        dangerouslySetInnerHTML={{
          __html: `${formattedTime}${goalRemaining}`
        }}
      />
    </RowStack>
  )
}

export const MilestoneBox = ({
  milestone,
  isCompleted = false,
  inverseColors = false
}) => (
  <Box
    sx={{
      width: '100%',
      padding: 4,
      backgroundColor: inverseColors
        ? colors.MetalGrey75
        : isCompleted
          ? colors.Green100
          : colors.MetalGrey50,
      borderRadius: 2
    }}>
    <RowStack spacing={4} justifyContent={'space-between'}>
      <IconContainer icon={<TourOutlinedIcon />} size={24} />
      <Typography variant='h5' flex={1}>
        {milestone.name}
      </Typography>
      {isCompleted && (
        <IconContainer
          icon={<CheckCircleIcon />}
          size={24}
          style={{
            color: !inverseColors ? colors.Green400 : colors.MidnightTeal300
          }}
        />
      )}
    </RowStack>
  </Box>
)

export const CardSkeleton = ({
  w = '100%',
  h = '100%',
  color = colors.MetalGrey50
}) => <BlankRect w={w} h={h} color={color} />
